import {
  ActivityButton,
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useInfiniteQuery,
  useMe,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { type Maybe, day, groupBy } from '@vrplatform/utils';
import { Text } from '@mantine/core';
import { useDisclosure, useSetState } from '@mantine/hooks';
import {
  formatOwnerName,
  formatUserName,
  getListingName,
  whereOwners,
} from '@vrplatform/ui-common';
import { useState } from 'react';
import {
  ACTIVITY_START_DATE,
  ActivityDeletedItemBadge,
  type ActivityDeltaJson,
  ActivityDrawer,
  type ActivityOperation,
  type ActivityPanelProps,
  type ActivityRow,
  type ActivityTable,
  getActivityActor,
  whereActorUserId,
} from '../../../../../drawers';
import { getOwnerActivityDetails, getOwnerActivityTables } from './_queries';

export const OwnerListActivityButton = () => {
  const [opened, handlers] = useDisclosure(false);

  return (
    <>
      <ActivityButton onClick={handlers.open} />
      <Drawer close={handlers.close} opened={opened} />
    </>
  );
};

type FilterState = {
  search: string;
  actorUserId: Maybe<string>;
  ownerId: Maybe<string>;
  op: Maybe<ActivityOperation>;
};

const Drawer = ({ close, opened }: { opened: boolean; close: () => void }) => {
  const [{ id: teamId }] = useTeam();
  const [dashboard] = useDashboard();
  const { id: meId } = useMe();

  const [filter, setFilter] = useSetState<FilterState>({
    actorUserId: null,
    search: '',
    ownerId: null,
    op: null,
  });

  const OWNER_ACTIVITY_TABLES = getOwnerActivityTables();

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      if (!args.teamId)
        return {
          aggregate: 0,
          list: [],
        };

      const where: gqlV2.audit_log_bool_exp = {
        tenantId: { _eq: args.teamId },
        tableName: { _in: args.OWNER_ACTIVITY_TABLES },
        op: args.op ? { _eq: args.op } : undefined,
        createdAt: { _gte: args.ACTIVITY_START_DATE },
        actorUserId: whereActorUserId(args.actorUserId),
        ownerId: args.ownerId ? { _eq: args.ownerId } : undefined,
        owner: args.search
          ? whereOwners({
              teamId: args.teamId,
              search: args.search,
            })
          : undefined,
      };

      const aggregate = q.auditLogAggregate({ where }).aggregate?.count() || 0;

      const logs = q
        .auditLogs({
          where,
          order_by: [{ createdAt: 'desc' }],
          offset,
          limit,
        })
        .map((log) => {
          const ownerId = log.ownerId;

          const getGroupRef = () => {
            if (
              log.tableName === 'listing_owner' ||
              log.tableName === 'owner_user_access'
            ) {
              return {
                op: 'update',
                ref: `${ownerId}-update-${day(log.createdAt).yyyymmdd()}`,
              };
            }

            return {
              op: log.op,
              ref: `${ownerId}-${log.op}-${day(log.createdAt).yyyymmdd()}`,
            };
          };

          const { op, ref } = getGroupRef();

          return {
            id: log.id,
            objectId: ownerId,
            op: op as ActivityOperation,
            tableName: log.tableName as ActivityTable,
            groupRef: ref,
            actor: getActivityActor(log),
            date: log.createdAt,
            ownerId,
            ownerName: formatOwnerName(log.owner),
          };
        });

      const list = Object.entries(groupBy(logs, 'groupRef')).map<ActivityRow>(
        ([, logs]) => {
          const log = logs[0];

          const actions: Record<ActivityOperation, string> = {
            insert: 'Created',
            delete: 'Deleted',
            update: 'Updated',
          };

          let op = log.op;

          if (log.tableName !== 'owner') {
            op = 'update';
          }

          if (logs.some((l) => l.op === 'insert')) {
            op = 'insert';
          }

          const getLabel = () => {
            return (
              <>
                <Text component="span" color="gray">
                  {actions[op]}
                </Text>{' '}
                {log.ownerName ? (
                  <Text fw={500} component="span">
                    {log.ownerName}
                  </Text>
                ) : (
                  <ActivityDeletedItemBadge
                    objectId={log.ownerId!}
                    type="owner"
                  />
                )}
              </>
            );
          };

          return {
            id: log.id,
            actor: log.actor,
            date: log.date,
            objectId: log.objectId!,
            icon: op,
            logId: log.id,
            op,
            label: getLabel(),
            detailIds:
              op === 'update' ? logs.map((x) => x.id as string) : undefined,
          };
        }
      );

      return {
        aggregate,
        list,
        count: logs.length,
      };
    },
    {
      queryKey: 'activity',
      variables: {
        teamId,
        search: filter.search?.trim(),
        dashboard,
        meId,
        ownerId: filter.ownerId,
        actorUserId: filter.actorUserId,
        op: filter.op,
        ACTIVITY_START_DATE,
        OWNER_ACTIVITY_TABLES,
      },
    }
  );

  return (
    <ActivityDrawer
      opened={opened}
      close={close}
      title="Owners"
      data={queryData}
      setFilter={setFilter}
      renderDetailPanel={renderDetailPanel}
      tables={OWNER_ACTIVITY_TABLES}
    >
      <OwnerFilter
        ownerId={filter.ownerId}
        setOwnerId={(ownerId) =>
          setFilter({
            ownerId,
          })
        }
      />
    </ActivityDrawer>
  );
};

const renderDetailPanel = ({ row, isExpanded }: ActivityPanelProps) => {
  const ids = row.detailIds || [];
  const { error, data, isLoading } = useQuery(
    (q, args) => {
      const logs = q
        .auditLogs({
          where: {
            id: { _in: args.ids },
          },
          order_by: [{ createdAt: 'desc' }],
        })
        .map((log) => {
          // logs can come from "owner", "listing_owner"
          return {
            date: log.createdAt,
            id: log.id,
            objectId: log.objectId!,
            op: log.op as ActivityOperation,
            tableName: log.tableName as ActivityTable,
            delta: (log.deltaJson() || []) as ActivityDeltaJson,
            listingId: log.listingId,
            listingName: getListingName(log.listing),
            userId: log.userId,
            userName: formatUserName(log.user),
          };
        });

      const owner =
        q
          .owners({
            where: {
              id: { _eq: args.objectId },
            },
            limit: 1,
          })
          .map((x) => ({
            id: x.id,
            isCompany: x.type === 'company',
            name: formatOwnerName(x),
          }))[0] || null;

      const deletedOwnerLog =
        q
          .auditLogs({
            where: { objectId: { _eq: args.objectId }, op: { _eq: 'delete' } },
          })
          .map((l) => {
            const delta = l.deltaJson() as ActivityDeltaJson;

            const isCompany =
              delta?.find((d) => d.key === 'type')?.value === 'company';

            return {
              id: l.ownerId,
              isCompany,
            };
          })[0] || null;

      return {
        logs,
        owner: owner || deletedOwnerLog,
        isCompany: !!owner?.isCompany || !!deletedOwnerLog?.isCompany,
      };
    },
    {
      queryKey: 'activity',
      skip: !ids?.length || !isExpanded,
      variables: {
        ids,
        objectId: row.objectId,
      },
    }
  );

  const rows =
    data?.logs.flatMap((log) => {
      return getOwnerActivityDetails(log, {
        owner: data.owner,
        listing: {
          id: log.listingId!,
          name: log.listingName,
        },
        user: {
          id: log.userId!,
          name: log.userName,
        },
      });
    }) || [];

  return {
    loading: isLoading,
    error,
    rows,
  };
};

const OwnerFilter = ({
  ownerId,
  setOwnerId,
}: {
  ownerId: Maybe<string>;
  setOwnerId: (id: Maybe<string>) => void;
}) => {
  const [{ id: teamId }] = useTeam();
  const gate = useSelectQueryGate({ value: ownerId });

  const [search, setSearch] = useState('');

  const queryData = useInfiniteQuery(
    (q, { teamId, search }, { limit, offset }) => {
      const where: gqlV2.owner_bool_exp = whereOwners({
        teamId,
        search,
      });

      const list = q
        .owners({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((res) => ({
          value: res.id,
          label: formatOwnerName(res),
        }));

      const aggregate = q.ownerAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchOptions,
      queryKey: ['owners'],
      variables: {
        teamId,
        search: search?.trim(),
      },
    }
  );

  const { data } = useQuery(
    (q, { ownerId }) => {
      if (!ownerId) return null;

      const value =
        q
          .owners({ where: { id: { _eq: ownerId } } })
          .map<SelectItem>((item) => ({
            label: formatOwnerName(item),
            value: item.id,
          }))[0] || null;

      return {
        value,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchValue,
      queryKey: ['owners'],
      keepPreviousData: true,
      variables: {
        ownerId,
      },
    }
  );

  const value = gate.hasValue ? data?.value || null : null;

  return (
    <Filter.Select
      value={value}
      setValue={(v) => setOwnerId(v?.value)}
      label="Owner"
      type="single"
      withinPortal
      onOpenChange={gate.onOpenChange}
      infiniteData={{ ...queryData, setSearch }}
    />
  );
};
